import POKEMON_TYPES_RAW from '../../../shared/pokemon-types.json'
import POKEMON_FORMS_RAW from '../../../shared/pokemon-forms.json'
import {
  type PokemonType as SharedPokemonType,
  getEffectiveness as sharedGetEffectiveness,
  getTypeEffectiveness as sharedGetTypeEffectiveness,
} from '../../../shared/type_effectiveness'

export type PokemonType = SharedPokemonType

export interface TypeInfo {
  id: PokemonType
  nameEn: string
  nameFr: string
  color: string
}

export const TYPES: TypeInfo[] = [
  { id: 'normal', nameEn: 'Normal', nameFr: 'Normal', color: '#A8A878' },
  { id: 'fire', nameEn: 'Fire', nameFr: 'Feu', color: '#F08030' },
  { id: 'water', nameEn: 'Water', nameFr: 'Eau', color: '#6890F0' },
  { id: 'electric', nameEn: 'Electric', nameFr: 'Électrik', color: '#F8D030' },
  { id: 'grass', nameEn: 'Grass', nameFr: 'Plante', color: '#78C850' },
  { id: 'ice', nameEn: 'Ice', nameFr: 'Glace', color: '#98D8D8' },
  { id: 'fighting', nameEn: 'Fighting', nameFr: 'Combat', color: '#C03028' },
  { id: 'poison', nameEn: 'Poison', nameFr: 'Poison', color: '#A040A0' },
  { id: 'ground', nameEn: 'Ground', nameFr: 'Sol', color: '#E0C068' },
  { id: 'flying', nameEn: 'Flying', nameFr: 'Vol', color: '#A890F0' },
  { id: 'psychic', nameEn: 'Psychic', nameFr: 'Psy', color: '#F85888' },
  { id: 'bug', nameEn: 'Bug', nameFr: 'Insecte', color: '#A8B820' },
  { id: 'rock', nameEn: 'Rock', nameFr: 'Roche', color: '#B8A038' },
  { id: 'ghost', nameEn: 'Ghost', nameFr: 'Spectre', color: '#705898' },
  { id: 'dragon', nameEn: 'Dragon', nameFr: 'Dragon', color: '#7038F8' },
  { id: 'dark', nameEn: 'Dark', nameFr: 'Ténèbres', color: '#705848' },
  { id: 'steel', nameEn: 'Steel', nameFr: 'Acier', color: '#B8B8D0' },
  { id: 'fairy', nameEn: 'Fairy', nameFr: 'Fée', color: '#EE99AC' },
]

export function getTypeInfo(type: PokemonType): TypeInfo {
  return TYPES.find((t) => t.id === type)!
}

export function getEffectiveness(attacker: PokemonType, defender: PokemonType): number {
  return sharedGetEffectiveness(attacker, defender)
}

export function getTypeEffectiveness(attackerType: PokemonType, defenderTypes: PokemonType[]): number {
  return sharedGetTypeEffectiveness(attackerType, defenderTypes)
}

// ── Slug → PokemonType mapping ──────────────────────────────────────
// Source unique : shared/pokemon-types.json
const POKEMON_TYPES = POKEMON_TYPES_RAW as unknown as Record<string, PokemonType | [PokemonType, PokemonType]>
const POKEMON_FORMS = POKEMON_FORMS_RAW as unknown as {
  formsByBaseSlug: Record<string, Array<{ slug: string; types: PokemonType[] }>>
}

const FORM_TYPES_BY_SLUG = new Map<string, PokemonType[]>()
for (const forms of Object.values(POKEMON_FORMS.formsByBaseSlug)) {
  for (const form of forms) {
    if (form.types.length > 0) FORM_TYPES_BY_SLUG.set(form.slug, form.types)
  }
}

type PokemonTypeSource = string | { slug: string; cosmeticFormSlug?: string | null }

function getTypesForSlug(slug: string): PokemonType[] {
  const formTypes = FORM_TYPES_BY_SLUG.get(slug)
  if (formTypes?.length) return formTypes

  const types = POKEMON_TYPES[slug] ?? 'normal'
  return Array.isArray(types) ? types : [types]
}

function getTypesFromSource(source: PokemonTypeSource): PokemonType[] {
  if (typeof source === 'object') {
    const { slug, cosmeticFormSlug } = source
    const form = cosmeticFormSlug
      ? POKEMON_FORMS.formsByBaseSlug[slug]?.find((entry) => entry.slug === cosmeticFormSlug)
      : null
    if (form?.types?.length) return form.types
    source = slug
  }

  return getTypesForSlug(source)
}

export function getPokemonType(source: PokemonTypeSource): PokemonType {
  return getTypesFromSource(source)[0] ?? 'normal'
}

export function getPokemonTypes(source: PokemonTypeSource): PokemonType[] {
  return getTypesFromSource(source)
}
